(function (global) {
  'use strict';

  const DEFAULT_CONFIG = Object.freeze({
    width: 720,
    height: 960,
    babyY: 115,
    babyMinX: 86,
    babyMaxX: 634,
    emitterOffsetX: 0,
    emitterOffsetY: 82,
    babyMinSpeed: 82,
    babyMaxSpeed: 178,
    babyAcceleration: 360,
    babyTurnMinMs: 650,
    babyTurnMaxMs: 1650,
    catcherY: 830,
    catcherWidth: 193.2,
    catcherHeight: 112,
    keyboardSpeed: 560,
    peeSpeed: 335,
    poopSpeed: 285,
    peeRadius: 18,
    poopRadius: 22,
    spawnMinMs: 850,
    spawnMaxMs: 1450,
    minimumSpawnMs: 420,
    maxActiveDrops: 2,
    reactionMs: 250
  });

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const moodForAnger = anger => anger >= 30 ? 'furious' : anger >= 25 ? 'angry' : anger >= 20 ? 'annoyed' : anger >= 10 ? 'concerned' : 'happy';

  function createGame(options = {}) {
    const config = Object.freeze({ ...DEFAULT_CONFIG, ...options.config });
    const random = typeof options.random === 'function' ? options.random : Math.random;
    const roll = () => clamp(Number(random()) || 0, 0, 0.999999999999);
    const between = (min, max) => min + (max - min) * roll();
    const state = {
      status: 'opening', score: 0, best: Math.max(0, Number(options.best) || 0), anger: 0,
      elapsedMs: 0, nextId: 1, spawnCountdownMs: 0, drops: [], lastReaction: '',
      baby: { x: config.width / 2, y: config.babyY, vx: 0, targetVx: 0, turnCountdownMs: 0 },
      catcherX: config.width / 2
    };

    const difficulty = () => Math.pow(1.1, Math.floor(state.score / 20));
    const babyMovementDifficulty = () => Math.pow(1.05, Math.floor(state.score / 20));
    const emitter = () => ({ x: state.baby.x + config.emitterOffsetX, y: state.baby.y + config.emitterOffsetY });
    const snapshot = () => ({
      status: state.status, score: state.score, best: state.best, anger: state.anger,
      mood: moodForAnger(state.anger), lastReaction: state.lastReaction,
      elapsedMs: state.elapsedMs, speedMultiplier: difficulty(), babySpeedMultiplier: babyMovementDifficulty(), spawnCountdownMs: state.spawnCountdownMs,
      baby: { ...state.baby }, catcherX: state.catcherX,
      drops: state.drops.map(drop => ({ ...drop }))
    });
    const nextSpawnDelay = () => Math.max(config.minimumSpawnMs, between(config.spawnMinMs, config.spawnMaxMs));

    function start() {
      state.status = 'playing';
      state.score = 0;
      state.anger = 0;
      state.elapsedMs = 0;
      state.nextId = 1;
      state.drops = [];
      state.lastReaction = '';
      state.baby = {
        x: config.width / 2, y: config.babyY, vx: 0, targetVx: 0,
        turnCountdownMs: between(config.babyTurnMinMs, config.babyTurnMaxMs)
      };
      state.catcherX = config.width / 2;
      state.spawnCountdownMs = nextSpawnDelay();
      return snapshot();
    }

    function pause() {
      if (state.status === 'playing') state.status = 'paused';
      return snapshot();
    }

    function resume() {
      if (state.status === 'paused') state.status = 'playing';
      return snapshot();
    }

    function setCatcherX(x) {
      if (state.status === 'playing') {
        state.catcherX = clamp(Number(x) || 0, config.catcherWidth / 2, config.width - config.catcherWidth / 2);
      }
      return state.catcherX;
    }

    function setBest(value) {
      state.best = Math.max(state.score, Math.max(0, Number(value) || 0));
      return state.best;
    }

    function spawnDrop(type) {
      if (state.status !== 'playing' || !['pee', 'poop'].includes(type)) return null;
      const source = emitter();
      const drop = {
        id: state.nextId++, type, x: source.x, y: source.y,
        speed: (type === 'pee' ? config.peeSpeed : config.poopSpeed) * difficulty(),
        radius: type === 'pee' ? config.peeRadius : config.poopRadius
      };
      state.drops.push(drop);
      return { ...drop };
    }

    function resolveDrop(id, outcome) {
      if (state.status !== 'playing' || !['caught', 'missed'].includes(outcome)) return false;
      const index = state.drops.findIndex(drop => drop.id === id);
      if (index < 0) return false;
      const [drop] = state.drops.splice(index, 1);
      const points = drop.type === 'pee' ? 5 : 10;
      if (outcome === 'caught') {
        const previousSpeedTier = Math.floor(state.score / 20);
        state.score += points;
        state.best = Math.max(state.best, state.score);
        const newSpeedTier = Math.floor(state.score / 20);
        if (newSpeedTier > previousSpeedTier) {
          const speedIncrease = Math.pow(1.05, newSpeedTier - previousSpeedTier);
          state.baby.vx *= speedIncrease;
          state.baby.targetVx *= speedIncrease;
        }
      } else {
        state.anger = Math.min(30, state.anger + points);
        state.lastReaction = drop.type === 'pee' ? 'missed-pee' : 'missed-poop';
        if (state.anger === 30) {
          state.status = 'game-over';
          state.drops = [];
          state.spawnCountdownMs = Infinity;
        }
      }
      return true;
    }

    function advanceBaby(deltaMs) {
      const baby = state.baby;
      baby.turnCountdownMs -= deltaMs;
      if (baby.turnCountdownMs <= 0) {
        const direction = baby.x < config.babyMinX + 70 ? 1 : baby.x > config.babyMaxX - 70 ? -1 : roll() < 0.5 ? -1 : 1;
        baby.targetVx = direction * between(config.babyMinSpeed, config.babyMaxSpeed) * babyMovementDifficulty();
        baby.turnCountdownMs = between(config.babyTurnMinMs, config.babyTurnMaxMs);
      }
      const maxChange = config.babyAcceleration * babyMovementDifficulty() * deltaMs / 1000;
      baby.vx += clamp(baby.targetVx - baby.vx, -maxChange, maxChange);
      baby.x = clamp(baby.x + baby.vx * deltaMs / 1000, config.babyMinX, config.babyMaxX);
      if (baby.x === config.babyMinX || baby.x === config.babyMaxX) {
        baby.targetVx = baby.x === config.babyMinX ? Math.abs(baby.targetVx) : -Math.abs(baby.targetVx);
        baby.vx = 0;
      }
    }

    const canSpawn = () => state.drops.length < config.maxActiveDrops;

    function advanceSlice(deltaMs) {
      state.elapsedMs += deltaMs;
      advanceBaby(deltaMs);
      for (const drop of [...state.drops]) {
        const oldY = drop.y;
        drop.y += drop.speed * deltaMs / 1000;
        const catchTop = config.catcherY - config.catcherHeight / 2;
        const catchBottom = config.catcherY + config.catcherHeight / 2;
        if (oldY <= catchBottom + drop.radius && drop.y >= catchTop - drop.radius && Math.abs(drop.x - state.catcherX) <= (config.catcherWidth / 2 + drop.radius)) {
          resolveDrop(drop.id, 'caught');
        } else if (drop.y - drop.radius > config.height) {
          resolveDrop(drop.id, 'missed');
        }
        if (state.status !== 'playing') return;
      }
      state.spawnCountdownMs -= deltaMs;
      if (state.spawnCountdownMs <= 0) {
        const type = roll() < 0.6 ? 'pee' : 'poop';
        if (canSpawn()) {
          spawnDrop(type);
          state.spawnCountdownMs = nextSpawnDelay();
        } else state.spawnCountdownMs = 80;
      }
    }

    function step(deltaMs) {
      if (state.status !== 'playing') return snapshot();
      let remaining = clamp(Number(deltaMs) || 0, 0, 2000);
      while (remaining > 0 && state.status === 'playing') {
        const slice = Math.min(remaining, 32);
        advanceSlice(slice);
        remaining -= slice;
      }
      return snapshot();
    }

    return Object.freeze({ config, start, pause, resume, setCatcherX, setBest, spawnDrop, resolveDrop, step, getState: snapshot, getEmitter: emitter });
  }

  const api = Object.freeze({ DEFAULT_CONFIG, createGame, moodForAnger });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.BBC_DIAPER_DASH_RULES = api;
})(typeof window !== 'undefined' ? window : null);
